import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../apiClient'; // Ensure this path is correct

// --- MUI Components ---
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Link as MuiLink,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack
} from '@mui/material';

// --- MUI Icons ---
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Known service options across both Horizon platforms
const SERVICE_OPTIONS = [
  // Hauliers (blue family)
  'National & Regional Haulage',
  'Fuel Transport',
  'Flatbed Cargo Transport',
  'Logistics Support',
  // Energies (red family)
  'Wholesale & Commercial Sourcing',
  'LPG & Gas Services',
  'Retail Fuel Network',
  'Market & Trading',
  'General Inquiry'
];

// Color mapping for service badges — Hauliers services use cool tones,
// Energies services use red-family tones to match the red Energies source chip.
const SERVICE_STYLES = {
  'National & Regional Haulage':      { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  'Fuel Transport':                   { bg: '#fff3e0', color: '#e65100', border: '#ffb74d' },
  'Flatbed Cargo Transport':          { bg: '#ede7f6', color: '#4527a0', border: '#b39ddb' },
  'Logistics Support':                { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  'Wholesale & Commercial Sourcing':  { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
  'LPG & Gas Services':               { bg: '#fbe9e7', color: '#d84315', border: '#ffab91' },
  'Retail Fuel Network':              { bg: '#fce4ec', color: '#c2185b', border: '#f48fb1' },
  'Market & Trading':                 { bg: '#ffcdd2', color: '#b71c1c', border: '#e57373' },
  'General Inquiry':                  { bg: '#ffe6e1', color: '#8e3232', border: '#e6b8b8' }
};

const renderServiceBadge = (service, size = 'small') => {
  if (!service) {
    return (
      <Chip
        label="No service"
        size={size}
        variant="outlined"
        sx={{ borderColor: '#cfd8dc', color: '#90a4ae', fontWeight: 500 }}
      />
    );
  }
  const style = SERVICE_STYLES[service] || { bg: '#eceff1', color: '#455a64', border: '#b0bec5' };
  return (
    <Chip
      label={service}
      size={size}
      sx={{
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        fontWeight: 600
      }}
    />
  );
};

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  // Filter state
  const [serviceFilter, setServiceFilter] = useState('all');

  // Detail Dialog State
  const [detailMessage, setDetailMessage] = useState(null);

  // Delete Dialog State
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // --- Data Fetching ---
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const response = await apiClient.get('/contact');
      setMessages(response.data);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setPageError('Failed to load inbox. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // --- Filtering ---
  const filteredMessages = useMemo(() => {
    if (serviceFilter === 'all') return messages;
    if (serviceFilter === 'none') return messages.filter(m => !m.service);
    return messages.filter(m => m.service === serviceFilter);
  }, [messages, serviceFilter]);

  // --- Delete Logic ---
  const handleOpenDeleteDialog = (id) => {
    setSelectedMessageId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedMessageId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!selectedMessageId) return;
    try {
      await apiClient.delete(`/contact/${selectedMessageId}`);
      setMessages(prev => prev.filter(msg => msg._id !== selectedMessageId));
      setSnackbar({ open: true, message: 'Message deleted successfully', severity: 'success' });
    } catch (err) {
      console.error('Error deleting message:', err);
      setSnackbar({ open: true, message: 'Failed to delete message.', severity: 'error' });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // --- Helper for Source Chips ---
  const renderSourceChip = (source) => {
    const safeSource = (source || "").toLowerCase();
    if (safeSource.includes("energies")) {
      return <Chip label="Energies" color="error" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
    } else if (safeSource.includes("hauliers")) {
      return <Chip label="Hauliers" color="primary" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
    } else {
      return <Chip label="General" size="small" variant="outlined" sx={{ borderColor: '#9e9e9e', color: '#757575' }} />;
    }
  };

  // --- Loading State ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Error State ---
  if (pageError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{pageError}</Alert>
        <Button startIcon={<RefreshIcon />} onClick={fetchMessages} sx={{ mt: 2 }}>
            Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* --- Page Header --- */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
         <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: '600', color: '#1e293b' }}>
            Inquiry Inbox
            </Typography>
            <Typography variant="body2" color="text.secondary">
            Manage inquiries from all Horizon platforms.
            </Typography>
         </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchMessages}
        >
          Refresh
        </Button>
      </Box>

      {/* --- Filter Bar --- */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 2,
        flexWrap: 'wrap'
      }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="service-filter-label">Filter by service</InputLabel>
          <Select
            labelId="service-filter-label"
            id="service-filter"
            value={serviceFilter}
            label="Filter by service"
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <MenuItem value="all">All services</MenuItem>
            {SERVICE_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
            <MenuItem value="none">— No service selected —</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary">
          Showing {filteredMessages.length} of {messages.length}
        </Typography>
      </Box>

      {/* --- Data Table --- */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '10px' }}>
        <Table sx={{ minWidth: 900 }} aria-label="messages table">
          <TableHead>
            <TableRow sx={{ '& th': {
              fontWeight: '600',
              backgroundColor: '#f9fafb',
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              py: 2
            } }}>
              <TableCell>Date</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Name / Email</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell width="40%">Message</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMessages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <EmailIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
                  <Typography>
                    {messages.length === 0
                      ? 'No messages found in inbox.'
                      : 'No messages match the current filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredMessages.map((msg) => (
                <TableRow
                  key={msg._id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: '#f9fafb' },
                    'td': { verticalAlign: 'top', py: 2 }
                  }}
                >
                  {/* Date */}
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.85rem' }}>
                    {formatDate(msg.createdAt || msg.date)}
                  </TableCell>

                  {/* Source Badge */}
                  <TableCell>
                    {renderSourceChip(msg.source)}
                  </TableCell>

                  {/* Name & Email */}
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {msg.name}
                    </Typography>
                    <MuiLink href={`mailto:${msg.email}`} underline="hover" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      {msg.email}
                    </MuiLink>
                  </TableCell>

                  {/* Service */}
                  <TableCell>
                    {renderServiceBadge(msg.service)}
                  </TableCell>

                  {/* Phone */}
                  <TableCell>
                    <MuiLink href={`tel:${msg.phone}`} underline="hover" sx={{ fontSize: '0.85rem', color: '#0f172a', whiteSpace: 'nowrap' }}>
                      {msg.phone || "—"}
                    </MuiLink>
                  </TableCell>

                  {/* Message Body */}
                  <TableCell sx={{ minWidth: 280 }}>
                    <Typography variant="body2" sx={{
                        color: '#334155',
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                      {msg.message}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        color="primary"
                        onClick={() => setDetailMessage(msg)}
                        size="small"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Message">
                        <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(msg._id)}
                        size="small"
                        >
                        <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* --- Detail Dialog --- */}
      <Dialog
        open={Boolean(detailMessage)}
        onClose={() => setDetailMessage(null)}
        maxWidth="sm"
        fullWidth
      >
        {detailMessage && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {detailMessage.name}
                </Typography>
                {renderSourceChip(detailMessage.source)}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDate(detailMessage.createdAt || detailMessage.date)}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {/* Service — prominent */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  Service Requested
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {renderServiceBadge(detailMessage.service, 'medium')}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Email</Typography>
                  <Typography variant="body2">
                    <MuiLink href={`mailto:${detailMessage.email}`} underline="hover">
                      {detailMessage.email}
                    </MuiLink>
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="overline" color="text.secondary">Phone</Typography>
                  <Typography variant="body2">
                    {detailMessage.phone
                      ? <MuiLink href={`tel:${detailMessage.phone}`} underline="hover">{detailMessage.phone}</MuiLink>
                      : '—'}
                  </Typography>
                </Box>

                {detailMessage.company && detailMessage.company !== 'N/A' && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">Company</Typography>
                    <Typography variant="body2">{detailMessage.company}</Typography>
                  </Box>
                )}

                {detailMessage.country && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">Country</Typography>
                    <Typography variant="body2">{detailMessage.country}</Typography>
                  </Box>
                )}

                <Divider />

                <Box>
                  <Typography variant="overline" color="text.secondary">Message</Typography>
                  <Typography variant="body2" sx={{
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                    color: '#334155',
                    mt: 0.5
                  }}>
                    {detailMessage.message}
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  handleOpenDeleteDialog(detailMessage._id);
                  setDetailMessage(null);
                }}
              >
                Delete
              </Button>
              <Button onClick={() => setDetailMessage(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* --- Delete Confirmation Dialog --- */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this message? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Snackbar Notification --- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}
